<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import type { ServiceCategory } from "@/entities/service-category";
import { Button, RadioGroup, RadioGroupItem } from "@/shared/ui";

/**
 * Вибір категорії для переміщення послуги (spec §7.3, 168-T5).
 * Поточна категорія позначена «зараз тут».
 */
const props = defineProps<{
  currentCategoryId: string | null;
  categories: ServiceCategory[];
}>();

const emit = defineEmits<{
  select: [categoryId: string | null];
  cancel: [];
}>();

const { t } = useI18n();

const selected = ref<string>(props.currentCategoryId ?? "__none__");

function onConfirm(): void {
  const id = selected.value === "__none__" ? null : selected.value;
  emit("select", id);
}
</script>

<template>
  <div class="move-picker">
    <RadioGroup v-model="selected" class="move-picker__list" data-testid="move-picker">
      <!-- Uncategorized option -->
      <div
        class="move-picker__item"
        :class="{ 'move-picker__item--selected': selected === '__none__' }"
        data-testid="move-option-none"
      >
        <RadioGroupItem value="__none__" :label="t('services.categories.uncategorized')" />
        <span
          v-if="currentCategoryId === null"
          class="move-picker__current-hint text-xs text-muted-foreground"
          data-testid="current-category-hint"
        >{{ t("services.detail.currentCategoryHint") }}</span>
      </div>

      <!-- Named categories -->
      <div
        v-for="cat in categories"
        :key="cat.id"
        class="move-picker__item"
        :class="{ 'move-picker__item--selected': selected === cat.id }"
        :data-testid="`move-option-${cat.id}`"
      >
        <RadioGroupItem :value="cat.id" :label="cat.name" />
        <span
          v-if="currentCategoryId === cat.id"
          class="move-picker__current-hint text-xs text-muted-foreground"
          data-testid="current-category-hint"
        >{{ t("services.detail.currentCategoryHint") }}</span>
      </div>
    </RadioGroup>

    <div class="move-picker__footer">
      <Button
        variant="ghost"
        size="sm"
        data-testid="move-cancel"
        @click="emit('cancel')"
      >
        {{ t("services.actions.cancel") }}
      </Button>
      <Button
        variant="default"
        size="sm"
        data-testid="move-confirm"
        @click="onConfirm"
      >
        {{ t("services.actions.moveHere") }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.move-picker {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-block-start: 0.5rem;
}

.move-picker__list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.move-picker__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  min-height: 2.75rem;
}

.move-picker__current-hint {
  flex-shrink: 0;
}

.move-picker__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-block-start: 0.5rem;
}
</style>
